import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  SpinnerGap,
  Calendar,
  Clock,
  Globe,
  Copy,
  CheckCircle,
  Lightning,
  ArrowRight,
  Link as LinkIcon,
  CalendarBlank,
  Wrench,
} from "phosphor-react";
import { useEntitlementsWithFetch } from "../contexts/EntitlementContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const TIME_OPTIONS = [];
for (let h = 6; h <= 22; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour = h.toString().padStart(2, "0");
    const min = m.toString().padStart(2, "0");
    TIME_OPTIONS.push(`${hour}:${min}`);
  }
}

const SERVICE_OPTIONS = [
  { value: "general", label: "General Service" },
  { value: "repair", label: "Repair" },
  { value: "installation", label: "Installation" },
  { value: "maintenance", label: "Maintenance" },
  { value: "inspection", label: "Inspection" },
  { value: "emergency", label: "Emergency" },
  { value: "consultation", label: "Consultation" },
];

export default function BookingSettings() {
  const navigate = useNavigate();
  const { isFeatureActive, isEntitled, shouldShowUpgradeCTA, fetched } = useEntitlementsWithFetch();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bookingUrl, setBookingUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [notEntitled, setNotEntitled] = useState(false);
  const [featureInactive, setFeatureInactive] = useState(false);
  const [featureDetails, setFeatureDetails] = useState(null);
  
  const [settings, setSettings] = useState({
    enabled: false,
    business_hours: {},
    eligible_services: [],
    booking_buffer_days: 1,
    max_advance_days: 30,
    default_duration: 60,
    require_phone: true,
    confirmation_message: "",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/tenants/booking-settings`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.error === "feature_inactive") {
          setFeatureInactive(true);
          setLoading(false);
          return;
        }
        
        if (data.error === "not_entitled") {
          setNotEntitled(true);
          setFeatureDetails(data.feature);
          setLoading(false);
          return;
        }
        
        if (data.entitled) {
          setSettings(data.settings);
          setBookingUrl(`${window.location.origin}${data.booking_url}`);
        }
      }
    } catch (error) {
      console.error("Error fetching booking settings:", error);
      toast.error("Failed to load booking settings");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/tenants/booking-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        toast.success("Booking settings saved!");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateBusinessHours = (day, field, value) => {
    setSettings((prev) => ({
      ...prev,
      business_hours: {
        ...prev.business_hours,
        [day]: {
          ...prev.business_hours[day],
          [field]: value,
        },
      },
    }));
  };

  const toggleService = (service) => {
    setSettings((prev) => ({
      ...prev,
      eligible_services: prev.eligible_services.includes(service)
        ? prev.eligible_services.filter((s) => s !== service)
        : [...prev.eligible_services, service],
    }));
  };

  const copyBookingUrl = () => {
    navigator.clipboard.writeText(bookingUrl);
    setCopied(true);
    toast.success("Booking URL copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-[#FF6600]" />
      </div>
    );
  }

  // Feature is globally inactive - hide everything
  if (featureInactive) {
    return (
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="booking-settings-inactive">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/settings")}
            className="border-2 border-zinc-200"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
              Online Booking
            </h1>
            <p className="text-zinc-500">Feature not available</p>
          </div>
        </div>
        
        <Card className="border-2 border-zinc-200">
          <CardContent className="p-8 text-center">
            <Calendar size={48} className="mx-auto text-zinc-300 mb-4" />
            <h2 className="font-heading text-lg font-bold uppercase mb-2">Coming Soon</h2>
            <p className="text-zinc-500">Online booking is not currently available.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User not entitled - show CTA
  if (notEntitled) {
    return (
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="booking-settings-not-entitled">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/settings")}
            className="border-2 border-zinc-200"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
              Online Booking
            </h1>
            <p className="text-zinc-500">Let customers book appointments online</p>
          </div>
        </div>
        
        <Card className="border-2 border-[#FF6600]/30 bg-gradient-to-r from-[#FF6600]/5 to-transparent">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="w-16 h-16 rounded-full bg-[#FF6600]/20 flex items-center justify-center flex-shrink-0">
                <Calendar size={32} className="text-[#FF6600]" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-xl font-bold uppercase mb-2">
                  Enable Online Booking
                </h2>
                <p className="text-zinc-600 mb-4">
                  Let your customers book appointments 24/7 directly from your website. 
                  Set your business hours, choose which services can be booked, and automatically 
                  create jobs when customers schedule.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                    <CheckCircle size={12} className="mr-1" />
                    24/7 Availability
                  </Badge>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                    <CalendarBlank size={12} className="mr-1" />
                    Auto-create Jobs
                  </Badge>
                  <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                    <Globe size={12} className="mr-1" />
                    Public Booking Page
                  </Badge>
                </div>
                {featureDetails && featureDetails.price_monthly && (
                  <p className="text-sm text-zinc-500">
                    Starting at <span className="font-bold text-[#FF6600]">${featureDetails.price_monthly.toFixed(2)}/mo</span>
                  </p>
                )}
              </div>
              <Button
                onClick={() => navigate("/settings/upgrades")}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white px-6"
                data-testid="upgrade-cta-btn"
              >
                <Lightning size={18} className="mr-2" />
                Upgrade Now
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is entitled - show full settings
  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="booking-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/settings")}
            className="border-2 border-zinc-200"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
              Online Booking
            </h1>
            <p className="text-zinc-500">Let customers book appointments online</p>
          </div>
        </div>
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
          data-testid="save-settings-btn"
        >
          {saving ? <SpinnerGap size={18} className="animate-spin" /> : "Save Settings"}
        </Button>
      </div>

      {/* Enable Toggle & Booking URL */}
      <Card className="border-2 border-zinc-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <Globe size={20} className="text-[#FF6600]" />
                Booking Status
              </CardTitle>
              <CardDescription>Enable or disable online booking for your customers</CardDescription>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              data-testid="booking-enabled-switch"
            />
          </div>
        </CardHeader>
        <CardContent>
          {settings.enabled && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-sm">
              <Label className="text-sm text-emerald-800 mb-2 block">Your Booking URL</Label>
              <div className="flex gap-2">
                <Input
                  value={bookingUrl}
                  readOnly
                  className="font-mono text-sm bg-white"
                />
                <Button
                  variant="outline"
                  onClick={copyBookingUrl}
                  className="border-2"
                  data-testid="copy-url-btn"
                >
                  {copied ? <CheckCircle size={18} className="text-emerald-600" /> : <Copy size={18} />}
                </Button>
              </div>
              <p className="text-xs text-emerald-700 mt-2">
                Share this link on your website or social media
              </p>
            </div>
          )}
          {!settings.enabled && (
            <p className="text-zinc-500 text-sm">
              Enable booking to get your shareable booking URL
            </p>
          )}
        </CardContent>
      </Card>

      {/* Business Hours */}
      <Card className="border-2 border-zinc-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
            <Clock size={20} className="text-[#FF6600]" />
            Business Hours
          </CardTitle>
          <CardDescription>Set when customers can book appointments</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {DAYS.map((day) => {
              const daySettings = settings.business_hours[day] || { start: "09:00", end: "17:00", closed: false };
              return (
                <div
                  key={day}
                  className={`flex items-center gap-4 p-3 rounded-sm ${daySettings.closed ? "bg-zinc-100" : "bg-white border border-zinc-200"}`}
                  data-testid={`hours-${day}`}
                >
                  <div className="w-28">
                    <span className="font-medium capitalize">{day}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={!daySettings.closed}
                      onCheckedChange={(checked) => updateBusinessHours(day, "closed", !checked)}
                    />
                    <span className="text-sm text-zinc-600">{daySettings.closed ? "Closed" : "Open"}</span>
                  </div>
                  {!daySettings.closed && (
                    <>
                      <Select
                        value={daySettings.start}
                        onValueChange={(v) => updateBusinessHours(day, "start", v)}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_OPTIONS.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-zinc-400">to</span>
                      <Select
                        value={daySettings.end}
                        onValueChange={(v) => updateBusinessHours(day, "end", v)}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_OPTIONS.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Eligible Services */}
      <Card className="border-2 border-zinc-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
            <Wrench size={20} className="text-[#FF6600]" />
            Bookable Services
          </CardTitle>
          <CardDescription>Choose which services customers can book online</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {SERVICE_OPTIONS.map((service) => (
              <label
                key={service.value}
                className={`flex items-center gap-3 p-3 rounded-sm border-2 cursor-pointer transition-all ${
                  settings.eligible_services.includes(service.value)
                    ? "border-[#FF6600] bg-[#FF6600]/5"
                    : "border-zinc-200 hover:border-zinc-300"
                }`}
              >
                <Checkbox
                  checked={settings.eligible_services.includes(service.value)}
                  onCheckedChange={() => toggleService(service.value)}
                />
                <span className="text-sm">{service.label}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Booking Options */}
      <Card className="border-2 border-zinc-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
            <CalendarBlank size={20} className="text-[#FF6600]" />
            Booking Options
          </CardTitle>
          <CardDescription>Configure booking rules and defaults</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Minimum Days in Advance</Label>
              <Input
                type="number"
                min={0}
                max={30}
                value={settings.booking_buffer_days}
                onChange={(e) => setSettings({ ...settings, booking_buffer_days: parseInt(e.target.value) || 1 })}
                className="mt-1"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Customers must book at least this many days ahead
              </p>
            </div>
            <div>
              <Label>Maximum Days in Advance</Label>
              <Input
                type="number"
                min={1}
                max={90}
                value={settings.max_advance_days}
                onChange={(e) => setSettings({ ...settings, max_advance_days: parseInt(e.target.value) || 30 })}
                className="mt-1"
              />
              <p className="text-xs text-zinc-500 mt-1">
                How far ahead customers can book
              </p>
            </div>
            <div>
              <Label>Default Appointment Duration (minutes)</Label>
              <Select
                value={settings.default_duration.toString()}
                onValueChange={(v) => setSettings({ ...settings, default_duration: parseInt(v) })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                  <SelectItem value="180">3 hours</SelectItem>
                  <SelectItem value="240">4 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 p-3 border-2 border-zinc-200 rounded-sm">
              <Switch
                checked={settings.require_phone}
                onCheckedChange={(checked) => setSettings({ ...settings, require_phone: checked })}
              />
              <div>
                <Label>Require Phone Number</Label>
                <p className="text-xs text-zinc-500">Customers must provide a phone number</p>
              </div>
            </div>
          </div>
          
          <div>
            <Label>Confirmation Message</Label>
            <Textarea
              value={settings.confirmation_message}
              onChange={(e) => setSettings({ ...settings, confirmation_message: e.target.value })}
              placeholder="Thank you for booking! We will contact you shortly to confirm your appointment."
              className="mt-1"
              rows={3}
            />
            <p className="text-xs text-zinc-500 mt-1">
              Message shown after a booking is submitted (optional)
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
